import { useEffect, useState } from "react";
import { Button, Checkbox, Form, Input, Modal, Select, Table, Tag, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { PageHeader } from "../../components/PageHeader";
import { api } from "../../helpers/api";
import { useApi } from "../../helpers/useApi";
import type { Permission, Role } from "../../api-models";

export default function RolesPage() {
  const { request } = useApi();
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [editing, setEditing] = useState<Role | null>(null);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  async function load() {
    const [r, p] = await Promise.all([api.get<Role[]>("/roles"), api.get<Permission[]>("/permissions")]);
    setRoles(r.data);
    setPermissions(p.data);
  }

  useEffect(() => {
    load();
  }, []);

  function openEdit(role: Role | null) {
    setEditing(role);
    form.setFieldsValue(role ?? { scopeType: "SITE", permissions: [] });
    setOpen(true);
  }

  async function handleSubmit() {
    const values = await form.validateFields();
    if (editing) await request({ method: "PUT", url: `/roles/${editing.id}`, data: values });
    else await request({ method: "POST", url: "/roles", data: values });
    setOpen(false);
    load();
  }

  async function handleDelete(role: Role) {
    Modal.confirm({
      title: `Delete role "${role.name}"?`,
      onOk: async () => {
        await request({ method: "DELETE", url: `/roles/${role.id}` });
        message.success("Role deleted.");
        load();
      },
    });
  }

  return (
    <div>
      <PageHeader
        title="Roles"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openEdit(null)} data-tour="roles-new">
            New role
          </Button>
        }
      />
      <Table
        rowKey="id"
        dataSource={roles}
        columns={[
          { title: "Name", dataIndex: "name", render: (v, r) => (r.isSystem ? <Tag color="gold">{v}</Tag> : v) },
          { title: "Scope", dataIndex: "scopeType" },
          { title: "Permissions", render: (_, r) => r.permissions.length },
          { title: "Description", dataIndex: "description" },
          {
            title: "",
            render: (_, r) =>
              !r.isSystem && (
                <>
                  <Button type="link" onClick={() => openEdit(r)}>Edit</Button>
                  <Button type="link" danger onClick={() => handleDelete(r)}>Delete</Button>
                </>
              ),
          },
        ]}
      />
      <Modal title={editing ? "Edit role" : "New role"} open={open} onCancel={() => setOpen(false)} onOk={handleSubmit} width={640} destroyOnHidden>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="scopeType" label="Scope" rules={[{ required: true }]}>
            <Select
              options={[
                { value: "GLOBAL", label: "Global — the whole organisation" },
                { value: "SITE", label: "Site — one assigned site" },
                { value: "PROJECT", label: "Project — every site of one project" },
              ]}
            />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="permissions" label="Permissions">
            <Checkbox.Group options={permissions.map((p) => ({ value: p.name, label: p.name }))} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
